import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './RegisterPaymentModal.css';

const RegisterPaymentModal = ({ isOpen, onClose, clientData, onPaymentRegistered }) => {
  const [formData, setFormData] = useState({
    pasarela: 'BANRESERVAS',
    cantidad: '',
    fecha: new Date().toISOString().slice(0, 16).replace('T', ' ')
  });
  const [loading, setLoading] = useState(false);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [invoices, setInvoices] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    if (isOpen && clientData?.id) {
      loadInvoices();
    }
  }, [isOpen, clientData]);

  const loadInvoices = async () => {
    if (!clientData?.id) return;

    try {
      setLoadingInvoices(true);
      const response = await axios.post('/api/contacts/get-client-invoices', {
        idcliente: clientData.id
      });

      if (response.data.success && response.data.invoices) {
        setInvoices(response.data.invoices);
        // Calcular el monto total
        const total = response.data.invoices.reduce((sum, invoice) => {
          return sum + parseFloat(invoice.monto || invoice.total || 0);
        }, 0);
        setTotalAmount(total);
        setFormData(prev => ({
          ...prev,
          cantidad: total.toFixed(2)
        }));
      } else {
        // Si no hay facturas, usar el total de facturacion
        const total = parseFloat(clientData.facturacion?.total_facturas || 0);
        setTotalAmount(total);
        setFormData(prev => ({
          ...prev,
          cantidad: total.toFixed(2)
        }));
      }
    } catch (error) {
      console.error('Error cargando facturas:', error);
      // Si falla, usar el total de facturacion
      const total = parseFloat(clientData.facturacion?.total_facturas || 0);
      setTotalAmount(total);
      setFormData(prev => ({
        ...prev,
        cantidad: total.toFixed(2)
      }));
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      // Validar campos requeridos
      if (!formData.cantidad || parseFloat(formData.cantidad) <= 0) {
        setMessage({ type: 'error', text: 'El monto total es requerido y debe ser mayor a 0' });
        setLoading(false);
        return;
      }

      // Validar que tengamos facturas con IDs reales
      const validInvoiceIds = invoices
        .map(inv => inv.idfactura || inv.id)
        .filter(id => id && !id.toString().startsWith('virtual_') && !isNaN(parseInt(id)));

      if (validInvoiceIds.length === 0) {
        setMessage({ type: 'error', text: 'No se encontraron facturas con IDs válidos. Por favor, intenta recargar las facturas.' });
        setLoading(false);
        return;
      }

      // Registrar pago para todas las facturas no pagadas
      const response = await axios.post('/api/contacts/register-payment', {
        idcliente: clientData.id,
        pasarela: formData.pasarela,
        cantidad: parseFloat(formData.cantidad),
        fecha: formData.fecha,
        invoices: validInvoiceIds
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: response.data.mensaje || 'Pago registrado correctamente' });
        
        // Limpiar formulario
        setFormData({
          pasarela: 'BANRESERVAS',
          cantidad: totalAmount.toFixed(2),
          fecha: new Date().toISOString().slice(0, 16).replace('T', ' ')
        });

        // Notificar al componente padre
        if (onPaymentRegistered) {
          onPaymentRegistered();
        }

        // Cerrar modal después de 2 segundos
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setMessage({ type: 'error', text: response.data.error || 'Error al registrar el pago' });
      }
    } catch (error) {
      console.error('Error registrando pago:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Error al registrar el pago';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="register-payment-modal-overlay" onClick={onClose}>
      <div className="register-payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="register-payment-modal-header">
          <h2>💳 Registrar Pago</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="register-payment-form">
          {message.text && (
            <div className={`register-payment-message ${message.type}`}>
              {message.text}
            </div>
          )}

          {clientData && (
            <div className="client-info-summary">
              <p><strong>Cliente:</strong> {clientData.nombre || 'N/A'}</p>
              <p><strong>ID:</strong> {clientData.id}</p>
              <p><strong>Deuda Actual:</strong> ${clientData.facturacion?.total_facturas || '0.00'}</p>
              <p><strong>Facturas No Pagadas:</strong> {clientData.facturacion?.facturas_nopagadas || 0}</p>
            </div>
          )}

          {loadingInvoices && (
            <div className="loading-invoices">
              <p>Cargando facturas...</p>
            </div>
          )}

          {invoices.length > 0 && (
            <div className="invoices-summary">
              <p><strong>Facturas a pagar ({invoices.length}):</strong></p>
              <ul className="invoices-list">
                {invoices.slice(0, 5).map((invoice, index) => (
                  <li key={index}>
                    Factura #{invoice.id || invoice.idfactura || index + 1}: ${parseFloat(invoice.monto || invoice.total || 0).toFixed(2)}
                  </li>
                ))}
                {invoices.length > 5 && (
                  <li>... y {invoices.length - 5} factura(s) más</li>
                )}
              </ul>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="pasarela">
              Pasarela de Pago
            </label>
            <select
              id="pasarela"
              name="pasarela"
              value={formData.pasarela}
              onChange={handleChange}
            >
              <option value="BANRESERVAS">BANRESERVAS</option>
              <option value="BANCO POPULAR">BANCO POPULAR</option>
              <option value="BANCO BHD LEON">BANCO BHD LEON</option>
            </select>
            <small className="form-help">
              Banco utilizado para el pago
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="cantidad">
              Monto Total a Pagar <span className="required">*</span>
            </label>
            <input
              type="number"
              id="cantidad"
              name="cantidad"
              value={formData.cantidad}
              onChange={handleChange}
              placeholder="0.00"
              required
              min="0"
              step="0.01"
            />
            <small className="form-help">
              Monto total calculado automáticamente de las facturas no pagadas
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="fecha">
              Fecha del Pago
            </label>
            <input
              type="datetime-local"
              id="fecha"
              name="fecha"
              value={formData.fecha}
              onChange={handleChange}
            />
            <small className="form-help">
              Fecha y hora del pago
            </small>
          </div>

          <div className="register-payment-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-button"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="submit-button"
              disabled={loading}
            >
              {loading ? 'Registrando...' : '💳 Registrar Pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterPaymentModal;

